<template>
  <two-column-layout>
    <card-container>
      <h2>{{ $t(i18n.starting_date_step.title) }}</h2>
      <p>{{ $t(i18n.starting_date_step.description) }}</p>

      <date-input-fragment
        :label="$t(i18n.policy_start_date.label)"
        show-info
        v-model="form.startDate"
        @blur="startDateChanged"
        @info-clicked="onInfoClicked('REGISTER_SINGLE_VEHICLE_INFO_STARTING_DATE')"
      >
        <template #result>
          <field-error-messages :validation-state="$v.form.startDate" :validation-messages="customValidationMessages" />
        </template>
      </date-input-fragment>

      <radio-group-fragment
          v-if="isHasDamageVisible"
          :label="$t(i18n.radio_group.has_damage.label)"
          group-name="group has damage"
          :options="radioGroupHasDamageOptions"
          :is-invalid="isFieldInvalid($v.form.hasDamage)"
          show-info
          v-model="form.hasDamage"
          @info-clicked="onInfoClicked('REGISTER_SINGLE_VEHICLE_INFO_STARTING_DATE_HAS_DAMAGE')"
      >
        <span>{{ $t(i18n.radio_group.has_damage.description) }}</span>
        <template #result>
          <field-error-messages :validation-state="$v.form.hasDamage"/>
        </template>
      </radio-group-fragment>

      <div v-if="!hasDamageValueIsValid">
        <media-item-info-block variant="warning" icon="phone" item-tag="REGISTER_SINGLE_VEHICLE_INVALID_HAS_DAMAGE" />
      </div>

      <div v-if="hasDamageValueIsValid">
        <fragment-separator></fragment-separator>

        <text-fragment :label="$t(i18n.policy_provisional_coverage_date.label)">
          {{ tempCoverageDate }}
        </text-fragment>

        <fragment-separator></fragment-separator>

        <text-input-fragment
          :label="$t(i18n.e_mail.label)"
          :placeholder="$t(i18n.e_mail.placeholder)"
          show-info
          v-model="form.email"
          @info-clicked="onInfoClicked('REGISTER_SINGLE_VEHICLE_INFO_EMAIL')"
        >
          <template #result>
            <field-error-messages :validation-state="$v.form.email" />
          </template>
        </text-input-fragment>
      </div>
    </card-container>

    <button-group direction="right" top-spacing="l">
      <button-fragment icon-before="back"
                       as-link
                       @click="previousStep()">{{$t(i18n.button.previous)}}</button-fragment>
      <button-fragment v-if="hasDamageValueIsValid"
                       @click="submit()">{{$t(i18n.button.submit)}}</button-fragment>
    </button-group>

    <template #right>
      <card-container>
        <receipt-group-fragment :label="$t(i18n.receipt.title)">
          <template #description>{{$t(i18n.receipt.description)}}</template>
          <receipt-item-fragment :label="$t(i18n.receipt.vehicle)">
            {{vehicle.licensePlate}}<br>
            {{vehicle.type}}<br>
            {{vehicle.info}}
          </receipt-item-fragment>
          <receipt-item-fragment :label="$t(i18n.receipt.usage)">{{$t(i18n.radio_group.vehicle_utilization[vehicleUtilization])}}</receipt-item-fragment>
          <receipt-item-fragment :label="$t(i18n.receipt.coverage.WA)">{{hasSelectedCoverage("WA")}}</receipt-item-fragment>
          <receipt-item-fragment :label="$t(i18n.receipt.coverage.casco.label)">{{$t(i18n.receipt.coverage.casco[coverage.CASCO])}}</receipt-item-fragment>

          <template v-for="coverage in getModelCoverages('ADDITIONAL')">
            <receipt-item-fragment :key="coverage.code" :label="$t(i18n.receipt.coverage[coverage.code])">{{hasSelectedCoverage(coverage.code)}}</receipt-item-fragment>
          </template>
        </receipt-group-fragment>
      </card-container>
      <media-item-card blank item-tag="REGISTER_SINGLE_VEHICLE_INFO_RECEIPT" />
    </template>
  </two-column-layout>
</template>

<script>
import {Utils} from "@bvm/core";
import {createNamespacedHelpers} from "vuex";
import moment from "moment";
import {email, required, requiredIf} from "vuelidate/lib/validators";
import {resetTime, offsetDayOrAfter, todayNoTime} from "../../../validations";
import {
  ButtonFragment,
  ButtonGroup,
  CardContainer,
  DateInputFragment,
  FragmentSeparator,
  RadioGroupFragment,
  ReceiptGroupFragment,
  ReceiptItemFragment,
  TextFragment,
  TextInputFragment,
  TwoColumnLayout
} from "@bvm/forms";
import {getCurrentProfile, updateCurrentProfile} from "../../../api/profile-api";
import MediaItemModal from "../../shared/media-item-modal";
import MediaItemCard from "../components/media-item-card";
import FieldErrorMessages from "../../shared/field-error-messages";

const {mapState, mapMutations} = createNamespacedHelpers("registerSingleVehicle");

const i18nBuilder = new Utils.I18n.I18nBuilder("register_single_vehicle_form")
  .add("starting_date_step", b => b.add("title").add("description"))
  .add("button", b => b.add("previous").add("submit"))
  .add("radio_group", b => b
    .add("generic", b => b.add("yes").add("no"))
    .add("vehicle_utilization", b => b.add("label").add("leasing").add("teaching").add("renting").add("business"))
    .add("has_damage", b => b.add("label").add("description"))
  )
  .add("receipt", b => b
    .add("title").add("description")
    .add("vehicle")
    .add("usage")
    .add("coverage", c => c
      .add("WA").add("casco", d => d.add("label").add("limited").add("full").add("none"))
      .add("OVI").add("SVI").add("VRB").add("PH").add("VV")
    )
  )
  .add("policy_start_date", b => b.add("label"))
  .add("policy_provisional_coverage_date", b => b.add("label"))
  .add("e_mail", b => b.add("label").add("placeholder"))
  .add("validations", b => b.add("start_date"));

const i18nValidationBuilder = new Utils.I18n.I18nBuilder("validations_field")
    .add("date_today_or_after")
    .add("email_not_valid");

export const messageKeys = [...i18nBuilder.getKeys(), ...i18nValidationBuilder.getKeys()];

export default {
  name: "starting-date-step",
  components: {
    TwoColumnLayout,
    CardContainer,
    ButtonGroup,
    ButtonFragment,
    DateInputFragment,
    TextFragment,
    TextInputFragment,
    FragmentSeparator,
    RadioGroupFragment,
    ReceiptGroupFragment,
    ReceiptItemFragment,
    MediaItemCard,
    FieldErrorMessages,
    "media-item-info-block": () => import("../components/media-item-info-block")
  },
  props: {},
  data() {
    return {
      i18n: i18nBuilder.getData(),
      currentProfile: null,
      form: {
        startDate: null,
        hasDamage: null,
        email: null
      },
      customValidationMessages: {
        start_date: i18nBuilder.prefix + "_validations_start_date"
      }
    };
  },
  computed: {
    ...mapState(["vehicle", "vehicleUtilization", "coverage", "productModelCoverages", "startDate", "email"]),
    radioGroupHasDamageOptions() {
      return {
        y: this.$t(this.i18n.radio_group.generic.yes),
        n: this.$t(this.i18n.radio_group.generic.no)
      };
    },
    isStartDateInThePast() {
      return this.form.startDate && new Date(this.form.startDate) < todayNoTime();
    },
    isHasDamageVisible() {
      return this.isStartDateInThePast && !this.$v.form.startDate.$invalid;
    },
    hasDamageValueIsValid() {
      // Also valid if 'hasDamage' is not set. Is handled by vuelidate
      return !this.isHasDamageVisible || !this.form.hasDamage || this.form.hasDamage === 'n';
    },
    tempCoverageDate() {
      let date = this.form.startDate
        ? new Date(this.form.startDate)
        : new Date();

      // If start date is not today, don't show time
      if (resetTime(date).getTime() !== todayNoTime().getTime()) {
        return moment(date).format("DD-MM-YYYY");
      }

      // Set current time and show time
      date.setTime(new Date().getTime());
      return moment(date).format("DD-MM-YYYY HH:mm:ss");
    }
  },
  created() {
    this.form.startDate = this.startDate;
    this.form.email = this.email;

    if (!this.currentProfile) {
      this.fetchEmailFromCurrentProfile();
    }
  },
  methods: {
    ...mapMutations(["setStartDate", "setEmail"]),
    hasSelectedCoverage(coverageToCheck) {
      return this.coverage[coverageToCheck] === "y"
        ? this.$t(this.i18n.radio_group.generic.yes)
        : this.$t(this.i18n.radio_group.generic.no);
    },
    getModelCoverages() {
      let types = Array.from(arguments);
      return this.productModelCoverages
        .filter(coverage => types.includes(coverage.type))
        .sort((a, b) => a.code.localeCompare(b.code));
    },
    onInfoClicked(mediaItemTag) {
      this.$bvmModal.show(MediaItemModal, {
          itemTag: mediaItemTag
      }, {
          clickToClose: true
      });
    },
    previousStep(value) {
      this.$emit("previousStep", value);
    },
    submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.updateToState();
        this.updateEmailToProfile();
        this.$emit("submit");
      }
    },
    updateToState() {
      this.setStartDate(this.form.startDate);
      this.setEmail(this.form.email);
    },
    updateEmailToProfile() {
      // Update if e-mail is different
      if(!this.currentProfile || this.currentProfile.emailAddress === this.form.email) {
        return;
      }

      this.currentProfile.emailAddress = this.form.email;
      updateCurrentProfile(this.currentProfile);
    },
    fetchEmailFromCurrentProfile() {
      getCurrentProfile().then(result => {
        this.currentProfile = result;

        if (!this.form.email) {
          this.form.email = this.currentProfile.emailAddress;
        }
      }).catch(() => {
         this.currentProfile = null;
         // On error, fail silently, no e-mail address will be pre-filled
      });
    },
    startDateChanged() {
      // Reset has damage when date changes
      this.form.hasDamage = null;
      // Invoke validation
      this.$v.form.startDate.$touch();
    },
    isFieldInvalid(field) {
      return field.$dirty && field.$invalid;
    }
  },
  validations() {
    return {
      form: {
        startDate: {start_date: offsetDayOrAfter(-5)},
        hasDamage: {
          field_required: requiredIf(() => {
            return !this.$v.form.startDate.$invalid && this.isStartDateInThePast;
          })
        },
        email: {field_required: required, email_not_valid: email}
      }
    };
  }
};
</script>

<style lang="scss"></style>
